import Box from '@components/base/Box'
import MiniNav, { MiniNavInput } from '@components/base/MiniNav'
import { Button } from '@components/inputs/Button'
import { InputPicker } from '@components/inputs/InputPicker/InputPicker'
import {
  Event,
  FormInputType,
  TicketPurchaseInput,
} from '@graphql/__generated/graphql'
import { MdOutlineQuiz } from 'react-icons/md'

type RegistrationFieldsStepProps = {
  allTicketsRegistrationFieldsAreFilled: boolean
  alreadyJoined: boolean
  attendeesToGetRegFields: TicketPurchaseInput[]
  event?: Partial<Event>
  fieldVals: any[]
  hasAccount: boolean
  onBack: () => void
  onContinue: () => void
  onRegistrationFieldChange: (
    attendeeIndex: number,
    fieldName: string,
    value: unknown
  ) => void
  onRegistrationFieldNext: (attendeeIndex: number, attendeeCount: number) => void
  onRegistrationFieldPrevious: (attendeeIndex: number) => void
  registrationFieldErrors: Record<string, string | undefined>
  regFieldSelectedAttendee: MiniNavInput
  saving: boolean
  setRegFieldSelectedAttendee: (attendee: MiniNavInput) => void
}

const RegistrationFieldsStep = ({
  allTicketsRegistrationFieldsAreFilled,
  alreadyJoined,
  attendeesToGetRegFields,
  event,
  fieldVals,
  hasAccount,
  onBack,
  onContinue,
  onRegistrationFieldChange,
  onRegistrationFieldNext,
  onRegistrationFieldPrevious,
  registrationFieldErrors,
  regFieldSelectedAttendee,
  saving,
  setRegFieldSelectedAttendee,
}: RegistrationFieldsStepProps) => {
  return (
    <Box className="relative">
      <Box className="mb-2 text-lg">
        <MdOutlineQuiz className="inline-block w-8 h-8 mr-1" /> Ticket Holder
        Information
        {hasAccount && alreadyJoined && (
          <span className="text-xs pt-2 block">
            You have already joined this event. If you wish, you can go back and
            check the event`s front page for more details.
          </span>
        )}
      </Box>
      <Box className="size-full">
        <MiniNav
          className="w-full sm:px-6 mb-6"
          stage={regFieldSelectedAttendee}
          setStage={(data) => {
            setRegFieldSelectedAttendee({
              ...regFieldSelectedAttendee!,
              ...data,
            })
          }}
          navItems={attendeesToGetRegFields.map((attendee, index) => ({
            label: attendee.fullName,
            targetStage: index,
          }))}
        />
        {attendeesToGetRegFields.map((attendee, attendeeIndex, attendees) => {
          const attendeeKey = `${attendee.fullName}::${attendee.email}`
            .trim()
            .toLowerCase()
            .replaceAll(' ', '_')
          const isSelected = regFieldSelectedAttendee?.stage === attendeeIndex

          return (
            <Box key={attendeeKey}>
              {event?.registrationFields?.map((field) => {
                const fieldName = `${attendeeIndex}::${field.name
                  .replaceAll(' ', '_')
                  .toLowerCase()}`

                return (
                  <Box show={isSelected} key={fieldName} className="mb-8">
                    <InputPicker
                      ukey={`registration_field_input_${attendeeKey}`}
                      type={field.type ?? FormInputType.Text}
                      dateOnly={field.dateOnly ?? false}
                      validations={{
                        minLength: {
                          value: 1,
                          message: 'Field name must be at least 1 character',
                        },
                      }}
                      value={fieldVals?.[attendeeIndex]?.[field.name]}
                      onChange={(value) =>
                        onRegistrationFieldChange(
                          attendeeIndex,
                          field.name,
                          value
                        )
                      }
                      required={field.required ?? false}
                      label={field.name}
                      placeholder={field.name}
                      options={field.options ?? []}
                      error={registrationFieldErrors?.[fieldName]}
                      outputFormat="string"
                    />
                  </Box>
                )
              })}

              <Box
                show={isSelected}
                className="flex w-full justify-between items-center mt-4 mb-10"
              >
                <Button
                  className="mr-auto"
                  show={regFieldSelectedAttendee?.stage !== 0}
                  type="square"
                  title="Previous"
                  onClick={() =>
                    onRegistrationFieldPrevious(attendeeIndex)
                  }
                />
                <Button
                  className="ml-auto"
                  show={
                    regFieldSelectedAttendee?.stage !== attendees.length - 1
                  }
                  type="square"
                  title="Next"
                  onClick={() =>
                    onRegistrationFieldNext(attendeeIndex, attendees.length)
                  }
                />
              </Box>
            </Box>
          )
        })}
      </Box>

      <Box className="flex w-full mt-4">
        <Button
          title="Back"
          className="flex-2 mr-2"
          buttonType="submit"
          onClick={onBack}
        />
        <Button
          disabled={!allTicketsRegistrationFieldsAreFilled}
          title="Continue"
          className="flex-[8]"
          textColor={'white'}
          buttonType="submit"
          onClick={onContinue}
          loading={saving}
        />
      </Box>
    </Box>
  )
}

export default RegistrationFieldsStep
